"use client";
import profile from "@/assets/icons/profile.svg";

import React, { startTransition, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import ticket from "@/assets/icons/Ticket.svg";
import login from "@/assets/icons/login.svg";
import global from "@/assets/icons/global.svg";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import UserDropdown from "./UserDropdown";
import MobileUserMenu from "./MobileUserMenu";
import MobileMenuHeader from "./MobileMenuHeader";
import {
  fetchHomePage,
  fetchSearchStoreProducts,
  searchEvents,
  fetchGlobalData,
} from "@/utils/loaders";
import arrowDown from "@/assets/icons/arrow-down.svg";
import Cookies from "js-cookie";
import HeaderDropdown from "./HeaderDropdown";
import AppDownloadBar from "./AppDownloadBar";
import { div, span } from "framer-motion/client";
import { setUserLocale } from "@/locales/locale";
import { useTranslations } from "next-intl";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLanguageRefetch } from "@/hooks/useLanguageRefetch";
import SearchInput from "../inputs/SearchInput";
import defaultImage from "@/assets/icons/NewHomePage/default-event.png";
import Reyal from "@/assets/imgs/store/categories/reyalBlo.svg";

const LogInModal = dynamic(() => import("../new-home/LogInModal"), {
  ssr: false,
});

export default function Header({ logoReferral, position, token }: any) {
  const [data, setData] = useState<any>(null);
  const [profil, setProfil] = useState<any>({});
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [currency, setCurrency] = useState("SAR");
  const pathname = usePathname();
  const t = useTranslations();
  const { language, isLoading, changeLanguage } = useLanguage();
  const [showSearch, setShowSearch] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const [IsAppOpen, setIsAppOpen] = useState(true);
  const [showStore, setShowStore] = useState<number>(0);
  const router = useRouter();
  const searchParams = useSearchParams();
  const isStoreModule =
    pathname.startsWith("/store") || pathname.startsWith("/user/cart");

  if (pathname.includes("3d-event")) return null;

  useEffect(() => {
    const checkStoreStatus = async () => {
      try {
        const response = await fetchGlobalData();
        // console.log("Init response:", response); // Print for verification as requested
        if (
          response &&
          response.data &&
          typeof response.data.show_store !== "undefined"
        ) {
          setShowStore(Number(response.data.show_store));
        } else if (response && typeof response.show_store !== "undefined") {
          setShowStore(Number(response.show_store));
        }
      } catch (error) {
        console.error("Error fetching init data:", error);
      }
    };
    checkStoreStatus();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowSearch(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setShowSearch(false);
    setSearchKey("");
    setSearchResults([]);
  }, [pathname, isStoreModule]);

  const fetchHeaderSearchResults = async () => {
    try {
      if (isStoreModule) {
        const res = await fetchSearchStoreProducts(
          `search=${encodeURIComponent(searchKey)}&limit=20&page=1`,
        );
        setSearchResults(res.data?.items || []);
        return;
      }

      const res = await searchEvents(searchKey);
      setSearchResults(res.data?.items || []);
    } catch (error) {
      setSearchResults([]);
    }
  };

  useLanguageRefetch({
    refetchFunction: fetchHeaderSearchResults,
    dependencies: [searchKey],
  });
  const currencies = [
    { value: "SAR", label: language === "ar" ? "ريال" : "SAR" },
    { value: "USD", label: language === "ar" ? "دولار" : "USD" },
    { value: "EUR", label: language === "ar" ? "يورو" : "EUR" },
  ];

  const languages = [
    { value: "en", label: "EN" },
    { value: "ar", label: "AR" },
  ];

  const getData = async () => {
    try {
      const res = await fetchHomePage();
      setData(res?.data);
    } catch (error) {
      console.error("Error fetching home page data:", error);
    }
  };

  const getCookieData = () => {
    // Get cookies using js-cookie

    const profileCookie = Cookies.get("profile") || "{}";
    const currencyCookie = Cookies.get("currency");

    const profileData =
      profileCookie && profileCookie !== "undefined"
        ? JSON.parse(profileCookie)
        : {};

    setProfil(profileData);
    const fallbackCurrency = "SAR";
    const rawCurrency = (currencyCookie ?? "").trim();
    const rawCurrencyLower = rawCurrency.toLowerCase();
    const normalizedCurrency =
      rawCurrency &&
      rawCurrencyLower !== "undefined" &&
      rawCurrencyLower !== "null"
        ? rawCurrency.toUpperCase()
        : fallbackCurrency;
    setCurrency(normalizedCurrency);
  };

  // Use the language refetch hook to refetch data when language changes
  useLanguageRefetch({
    refetchFunction: async () => {
      await getData();
      getCookieData();
    },
  });

  useEffect(() => {
    getData();
    getCookieData();
  }, [language]);

  useEffect(() => {
    if (searchParams.get("login") === "1") {
      setModalIsOpen(true);
    }
  }, [searchParams]);

  const handleLanguageChange = (value: string) => {
    const newLanguage = language === "en" ? "ar" : "en";
    startTransition(() => {
      changeLanguage(newLanguage);
    });
  };

  // Hide header on mobile for /select-ticket/general path
  const shouldHideOnDetails = pathname === "/";
  const isPaddingInHome = pathname === "/";
  const isReferral = pathname.includes("/referral");
  const searchPlaceholder = isStoreModule
    ? t("store.searchPlaceholderHeader")
    : undefined;

  const renderSearchResults = () => {
    if (isStoreModule) {
      return searchResults.map((product: any, index: number) => (
        <div
          key={index}
          onMouseDown={() => {
            router.push(`/store/product-details/${product?.id}`);
          }}
          className="block hover:bg-[#111111] transition-colors"
        >
          <div className="p-4 border-b border-[#0000000F]">
            <div className="flex items-start gap-4">
              <img
                className="w-[100px] h-[100px] rounded-[16px] object-cover"
                src={product.image || defaultImage.src}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = defaultImage.src;
                }}
                alt={product.name || "product"}
              />

              <div className="flex-1">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <h4 className="text-[#D1D3D4] font-medium">{product.name}</h4>
                  {product?.label && (
                    <span className="shrink-0 rounded-[8px] bg-[#d9ac4f14] px-2 py-1 text-[10px] font-medium text-[#d9ac4f]">
                      {product.label}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <span className="font-bold text-[#D1D3D4]">
                    {product.price}
                  </span>
                  {product?.old_price && (
                    <span className="font-bold text-[#D1D3D4] line-through">
                      {product.old_price}
                    </span>
                  )}
                  <Image
                    src={Reyal}
                    alt="reyal"
                    width={14}
                    height={14}
                    className="h-[14px] w-[14px]"
                  />
                </div>

                {typeof product?.stock !== "undefined" && (
                  <div className="mt-2 text-xs text-[#D1D3D4]">
                    {product.stock}
                  </div>
                )}
              </div>

              <div
                className={`flex items-center ${language === "ar" ? "rotate-180" : ""}`}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="#D4AF37"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      ));
    }

    return searchResults.map((event: any) => (
      <div
        key={event?.id || event?.slug}
        onMouseDown={() => {
          router.push(`/tickets/${event?.slug}/select-ticket`);
        }}
        className="block hover:bg-[#111111] transition-colors"
      >
        <div className="p-4 border-b border-[#0000000F]">
          <div className="flex items-start gap-4">
            <img
              className="w-[100px] h-[100px] rounded-[16px] object-cover"
              src={event.image || "/default-event.png"}
              alt=""
            />
            <div className="flex-1">
              <h4 className="text-[#D1D3D4] font-medium mb-3">{event.title}</h4>
              <div className="flex items-center gap-4 text-sm text-[#D1D3D4]">
                <div className="flex items-center gap-2">
                  <img
                    src="https://cdn.grintahub.com/public/assets/web/images/deadline.png"
                    width="14"
                    height="14"
                    alt=""
                  />
                  <span>{event.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <img
                    src="https://cdn.grintahub.com/public/assets/web/images/pin.png"
                    width="12"
                    height="14"
                    alt=""
                  />
                  <span>{event.address}</span>
                </div>
              </div>
            </div>
            <div
              className={`flex items-center ${language === "ar" ? "rotate-180" : ""}`}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="#D4AF37"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    ));
  };

                  console.log("🚀 ~ Header ~ showStore:", showStore)
  return (
    <div
      className={`relative  pb-[3px] bg-transparent  ${shouldHideOnDetails ? "" : "!pb-[4px]  md:!pt-[4px] "} lg:px-10`}
      style={{
        borderBottom: "1px solid #D4AF37",
        boxShadow: "0 6px 9px 0 rgba(0, 0, 0, 0.05)",
      }}
    >
      {!isReferral && IsAppOpen && (
        <AppDownloadBar setIsAppOpen={setIsAppOpen} />
      )}
      <div
        className={`relative z-100 bg-transparent pt-[20px] pb-[17px] px-3 lg:px-10 mb-0 md:mb-[10px]${shouldHideOnDetails ? "" : "md:!pt-[4px]  "}`}
      >
        <div
          className={`xl:container ${isPaddingInHome ? "px-1 md:px-15" : "mx-auto"} pt-1 flex gap-4 md:gap-[64px] !mx-auto items-center ${shouldHideOnDetails ? "" : "!mb-0 md:!mb-1"}`}
        >
          {!isReferral && (
            <div className="shrink-0 flex items-center gap-2">
              <MobileMenuHeader
                categories={data?.categories}
                profil={profil}
                setModalIsOpen={setModalIsOpen}
                showStore={showStore}
              />
            </div>
          )}

          <Link href={"/"}>
            <svg
              viewBox="0 0 170 41"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={
                showStore === 1
                  ? "w-[100px] h-[33px] md:w-[170px] md:h-[41px]"
                  : "w-[115px] h-[33px] md:w-[170px] md:h-[41px]"
              }
            >
              <g clipPath="url(#clip0_155_124)">
                <path
                  d="M54.2228 3.5086C53.9876 3.5086 53.7666 3.45859 53.5527 3.37286C53.346 3.28714 53.1606 3.15855 53.0038 2.99424C52.847 2.82993 52.7187 2.64419 52.6331 2.43701C52.5405 2.2227 52.4977 1.99409 52.4977 1.7512C52.4977 1.50831 52.5405 1.28685 52.6331 1.06539C52.7187 0.858215 52.847 0.672474 53.0038 0.508165C53.1606 0.343855 53.346 0.222409 53.5527 0.129539C53.7666 0.0366683 53.9947 -0.00619507 54.2228 -0.00619507C54.4509 -0.00619507 54.6791 0.0438121 54.8929 0.129539C55.0997 0.215265 55.285 0.343855 55.4418 0.508165C55.5987 0.672474 55.727 0.858215 55.8125 1.06539C55.9052 1.2797 55.948 1.50831 55.948 1.7512C55.948 1.99409 55.9052 2.21555 55.8125 2.43701C55.727 2.64419 55.5987 2.82993 55.4418 2.99424C55.285 3.15855 55.0997 3.27999 54.8929 3.37286C54.6791 3.46573 54.4509 3.5086 54.2228 3.5086ZM54.2228 0.165258C53.3603 0.165258 52.6616 0.879647 52.6616 1.75834C52.6616 2.63704 53.3603 3.35143 54.2228 3.35143C55.0854 3.35143 55.784 2.63704 55.784 1.75834C55.784 0.879647 55.0854 0.165258 54.2228 0.165258Z"
                  fill="#D4AF37"
                />
                <path
                  d="M54.5436 1.89408C54.5436 1.89408 54.6077 1.87265 54.6363 1.85836C54.7147 1.8155 54.7788 1.75121 54.8216 1.67262C54.8644 1.59404 54.8858 1.50117 54.8858 1.39401C54.8858 1.28685 54.8644 1.19398 54.8216 1.1154C54.7788 1.03682 54.7147 0.972522 54.6363 0.929659C54.5578 0.886796 54.4652 0.865364 54.3582 0.865364H53.6311V2.70849H53.8877V1.9298H54.2727L54.6648 2.70849H54.9713L54.5436 1.90123V1.89408ZM53.8806 1.10826H54.3654C54.4153 1.10826 54.458 1.12254 54.4937 1.14398C54.5293 1.16541 54.5578 1.20113 54.5792 1.24399C54.6006 1.28685 54.6077 1.33686 54.6077 1.39401C54.6077 1.45116 54.6006 1.50117 54.5792 1.54403C54.5578 1.5869 54.5293 1.62262 54.4937 1.64405C54.458 1.66548 54.4153 1.67977 54.3654 1.67977H53.8806V1.10826Z"
                  fill="#D4AF37"
                />
                <path
                  d="M29.3936 5.53031L7.43715 11.424L8.4922 15.3746C9.2122 15.4032 9.91794 15.5246 10.5952 15.7318C13.2898 16.5462 15.5211 18.7036 16.2981 21.6255C17.2748 25.276 15.6922 29.0337 12.6482 30.9483L13.7033 34.8988L29.3864 30.6911V35.849L10.196 40.9997L6.66725 27.7907L9.06963 27.1477C10.9088 26.6548 11.9995 24.7545 11.5076 22.9185C11.0158 21.0754 9.11953 19.9824 7.28745 20.4753L4.88508 21.1183L3.9227 17.532L2.63954 12.7242L1.34924 7.91637L29.3936 0.372429V5.53031Z"
                  fill="#D4AF37"
                />
                <path
                  d="M58.8278 33.0914L29.3934 40.9926V35.8418L52.7471 29.5695L51.692 25.6189C50.972 25.5903 50.2591 25.4689 49.5819 25.2617C46.8873 24.4473 44.6631 22.2899 43.879 19.368C42.9023 15.7104 44.4849 11.9598 47.5288 10.0452L46.4738 6.09468L29.3934 10.6811V5.53031L49.9811 0.000946045L53.5098 13.21L51.1075 13.8529C49.2683 14.3459 48.1776 16.2461 48.6694 18.0821C49.1613 19.9252 51.0576 21.0183 52.8896 20.5253L55.292 19.8824L56.2544 23.4686L57.5375 28.2764L58.8207 33.0843L58.8278 33.0914Z"
                  fill="#D4AF37"
                />
                <path
                  d="M85.5605 8.23128L85.2968 9.75293C84.8191 12.4819 83.6073 14.6179 81.6469 16.161C79.6865 17.7112 77.3411 18.4828 74.5966 18.4828C71.852 18.4828 69.5495 17.5612 67.9954 15.7181C66.4413 13.875 65.8924 11.6318 66.3487 8.98853C66.8049 6.38102 68.0596 4.23071 70.1055 2.53761C72.1514 0.844504 74.5467 0.00152588 77.2841 0.00152588C79.109 0.00152588 80.6845 0.373008 82.0033 1.11597C83.3221 1.85894 84.3201 2.90909 85.0045 4.26643L81.1336 6.22385C80.2924 4.71649 78.8595 3.96638 76.835 3.96638C75.2382 3.96638 73.8623 4.45217 72.7146 5.41659C71.5669 6.38102 70.8469 7.65977 70.5689 9.25286C70.2695 10.8459 70.5261 12.1247 71.3459 13.0891C72.1657 14.0535 73.3847 14.5393 75.0243 14.5393C77.6619 14.5393 79.4584 13.6249 80.4279 11.7961H75.6873L76.3217 8.23843H85.5677L85.5605 8.23128Z"
                  fill="#D1D3D4"
                />
                <path
                  d="M94.9918 18.4822C92.3185 18.4822 90.2013 17.5535 88.6258 15.6889C87.0504 13.8244 86.5157 11.5955 87.0076 8.98795C87.4853 6.34472 88.7399 4.18726 90.7858 2.51559C92.8318 0.836781 95.284 0.000946045 98.1498 0.000946045C100.787 0.000946045 102.89 0.929651 104.459 2.79421C106.034 4.65876 106.583 6.88765 106.105 9.49517C105.614 12.1384 104.352 14.2959 102.313 15.9675C100.274 17.6463 97.8361 18.4822 94.9846 18.4822H94.9918ZM95.4409 14.5245C96.9878 14.5245 98.3779 14.0315 99.6183 13.0528C100.859 12.0741 101.614 10.8025 101.899 9.24513C102.178 7.68777 101.899 6.41616 101.073 5.43744C100.238 4.45873 99.0551 3.9658 97.5296 3.9658C96.004 3.9658 94.6425 4.45873 93.4448 5.43744C92.2472 6.41616 91.513 7.68777 91.2278 9.24513C90.9498 10.8168 91.1993 12.0955 91.9906 13.0671C92.7819 14.0387 93.9296 14.5245 95.4409 14.5245Z"
                  fill="#D1D3D4"
                />
                <path
                  d="M111.823 14.4173H118.41L117.697 18.4822H106.904L107.624 14.3959L110.162 0.000946045H114.361L111.823 14.4173Z"
                  fill="#D1D3D4"
                />
                <path
                  d="M130.193 0.000946045C132.617 0.000946045 134.563 0.936795 136.032 2.80135C137.5 4.6659 138.006 6.90194 137.55 9.50946C137.094 12.1313 135.882 14.2816 133.914 15.9675C131.947 17.6463 129.63 18.4893 126.957 18.4893H119.557L122.815 0.00808993H130.193V0.000946045ZM133.465 9.1094C133.743 7.54489 133.515 6.309 132.781 5.41601C132.04 4.51588 130.935 4.06582 129.459 4.06582H126.294L124.462 14.4173H127.37C128.931 14.4173 130.272 13.9315 131.377 12.9671C132.482 11.9955 133.18 10.7168 133.458 9.1094H133.465Z"
                  fill="#D1D3D4"
                />
                <path
                  d="M152.307 4.06582H145.057L144.508 7.15198H151.123L150.41 11.1097H143.809L143.225 14.4102H150.603L149.89 18.475H138.306L141.564 -0.00619507H153.005L152.292 4.05868L152.307 4.06582Z"
                  fill="#D1D3D4"
                />
                <path
                  d="M152.278 18.4822L155.564 0.000946045H158.715L163.998 10.381L165.801 0.000946045H170L166.771 18.4822H163.606L158.337 8.10211L156.477 18.4822H152.278Z"
                  fill="#D1D3D4"
                />
                <path
                  d="M79.9076 22.5113L79.5155 24.8331H74.0621L71.2248 40.9926H68.7868L71.624 24.8331H66.1991L66.5912 22.5113H79.9076Z"
                  fill="#D1D3D4"
                />
                <path
                  d="M82.3455 22.5113H84.7835L81.5257 40.9997H79.0876L82.3455 22.5113Z"
                  fill="#D1D3D4"
                />
                <path
                  d="M94.1649 40.9926C91.3704 40.9926 89.2247 40.0567 87.7205 38.1922C86.2164 36.3276 85.7031 34.0916 86.1807 31.4983C86.637 28.9194 87.8987 26.7762 89.9732 25.0688C92.0476 23.3615 94.4001 22.5113 97.0377 22.5113C98.827 22.5113 100.374 22.9043 101.664 23.6829C102.955 24.4616 103.917 25.5332 104.551 26.8905L102.363 28.0121C101.329 25.8761 99.5114 24.8045 96.9094 24.8045C94.8207 24.8045 92.9958 25.4689 91.4417 26.7905C89.8876 28.1121 88.9324 29.7695 88.5831 31.7555C88.2338 33.7558 88.5974 35.4132 89.6738 36.7205C90.7573 38.0279 92.3185 38.6779 94.3787 38.6779C95.7118 38.6779 96.9451 38.3922 98.0643 37.8135C99.1906 37.2349 100.117 36.4276 100.859 35.3918L102.89 36.6419C101.892 37.985 100.616 39.0423 99.0837 39.8281C97.5439 40.6068 95.9114 40.9997 94.172 40.9997L94.1649 40.9926Z"
                  fill="#D1D3D4"
                />
                <path
                  d="M121.411 22.5113L111.502 31.6198L118.303 40.9926H115.565L108.957 31.8841L107.353 40.9926H104.915L108.173 22.5042H110.611L109.149 30.8197L118.31 22.5042H121.418L121.411 22.5113Z"
                  fill="#D1D3D4"
                />
                <path
                  d="M134.185 24.8331H125.752L124.747 30.5125H132.517L132.097 32.8128H124.341L123.3 38.6779H131.861L131.469 40.9997H120.455L123.728 22.5113H134.585L134.192 24.8331H134.185Z"
                  fill="#D1D3D4"
                />
                <path
                  d="M149.469 22.5113L149.077 24.8331H143.624L140.787 40.9926H138.349L141.186 24.8331H135.761L136.153 22.5113H149.469Z"
                  fill="#D1D3D4"
                />
              </g>
              <defs>
                <clipPath id="clip0_155_124">
                  <rect width="170" height="41" fill="white" />
                </clipPath>
              </defs>
            </svg>

            {logoReferral && (
              <svg
                viewBox="0 0 170 41"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={
                  showStore === 1
                    ? "w-[100px] h-[33px] md:w-[170px] md:h-[41px]"
                    : "w-[160px] h-[30px] md:w-[170px] md:h-[41px]"
                }
              >
                <g clipPath="url(#clip0_155_124)">
                  <path
                    d="M54.2228 3.5086C53.9876 3.5086 53.7666 3.45859 53.5527 3.37286C53.346 3.28714 53.1606 3.15855 53.0038 2.99424C52.847 2.82993 52.7187 2.64419 52.6331 2.43701C52.5405 2.2227 52.4977 1.99409 52.4977 1.7512C52.4977 1.50831 52.5405 1.28685 52.6331 1.06539C52.7187 0.858215 52.847 0.672474 53.0038 0.508165C53.1606 0.343855 53.346 0.222409 53.5527 0.129539C53.7666 0.0366683 53.9947 -0.00619507 54.2228 -0.00619507C54.4509 -0.00619507 54.6791 0.0438121 54.8929 0.129539C55.0997 0.215265 55.285 0.343855 55.4418 0.508165C55.5987 0.672474 55.727 0.858215 55.8125 1.06539C55.9052 1.2797 55.948 1.50831 55.948 1.7512C55.948 1.99409 55.9052 2.21555 55.8125 2.43701C55.727 2.64419 55.5987 2.82993 55.4418 2.99424C55.285 3.15855 55.0997 3.27999 54.8929 3.37286C54.6791 3.46573 54.4509 3.5086 54.2228 3.5086ZM54.2228 0.165258C53.3603 0.165258 52.6616 0.879647 52.6616 1.75834C52.6616 2.63704 53.3603 3.35143 54.2228 3.35143C55.0854 3.35143 55.784 2.63704 55.784 1.75834C55.784 0.879647 55.0854 0.165258 54.2228 0.165258Z"
                    fill="#D4AF37"
                  />
                  <path
                    d="M54.5436 1.89408C54.5436 1.89408 54.6077 1.87265 54.6363 1.85836C54.7147 1.8155 54.7788 1.75121 54.8216 1.67262C54.8644 1.59404 54.8858 1.50117 54.8858 1.39401C54.8858 1.28685 54.8644 1.19398 54.8216 1.1154C54.7788 1.03682 54.7147 0.972522 54.6363 0.929659C54.5578 0.886796 54.4652 0.865364 54.3582 0.865364H53.6311V2.70849H53.8877V1.9298H54.2727L54.6648 2.70849H54.9713L54.5436 1.90123V1.89408ZM53.8806 1.10826H54.3654C54.4153 1.10826 54.458 1.12254 54.4937 1.14398C54.5293 1.16541 54.5578 1.20113 54.5792 1.24399C54.6006 1.28685 54.6077 1.33686 54.6077 1.39401C54.6077 1.45116 54.6006 1.50117 54.5792 1.54403C54.5578 1.5869 54.5293 1.62262 54.4937 1.64405C54.458 1.66548 54.4153 1.67977 54.3654 1.67977H53.8806V1.10826Z"
                    fill="#D4AF37"
                  />
                  <path
                    d="M29.3936 5.53031L7.43715 11.424L8.4922 15.3746C9.2122 15.4032 9.91794 15.5246 10.5952 15.7318C13.2898 16.5462 15.5211 18.7036 16.2981 21.6255C17.2748 25.276 15.6922 29.0337 12.6482 30.9483L13.7033 34.8988L29.3864 30.6911V35.849L10.196 40.9997L6.66725 27.7907L9.06963 27.1477C10.9088 26.6548 11.9995 24.7545 11.5076 22.9185C11.0158 21.0754 9.11953 19.9824 7.28745 20.4753L4.88508 21.1183L3.9227 17.532L2.63954 12.7242L1.34924 7.91637L29.3936 0.372429V5.53031Z"
                    fill="#D4AF37"
                  />
                  <path
                    d="M58.8278 33.0914L29.3934 40.9926V35.8418L52.7471 29.5695L51.692 25.6189C50.972 25.5903 50.2591 25.4689 49.5819 25.2617C46.8873 24.4473 44.6631 22.2899 43.879 19.368C42.9023 15.7104 44.4849 11.9598 47.5288 10.0452L46.4738 6.09468L29.3934 10.6811V5.53031L49.9811 0.000946045L53.5098 13.21L51.1075 13.8529C49.2683 14.3459 48.1776 16.2461 48.6694 18.0821C49.1613 19.9252 51.0576 21.0183 52.8896 20.5253L55.292 19.8824L56.2544 23.4686L57.5375 28.2764L58.8207 33.0843L58.8278 33.0914Z"
                    fill="#D4AF37"
                  />
                  <path
                    d="M85.5605 8.23128L85.2968 9.75293C84.8191 12.4819 83.6073 14.6179 81.6469 16.161C79.6865 17.7112 77.3411 18.4828 74.5966 18.4828C71.852 18.4828 69.5495 17.5612 67.9954 15.7181C66.4413 13.875 65.8924 11.6318 66.3487 8.98853C66.8049 6.38102 68.0596 4.23071 70.1055 2.53761C72.1514 0.844504 74.5467 0.00152588 77.2841 0.00152588C79.109 0.00152588 80.6845 0.373008 82.0033 1.11597C83.3221 1.85894 84.3201 2.90909 85.0045 4.26643L81.1336 6.22385C80.2924 4.71649 78.8595 3.96638 76.835 3.96638C75.2382 3.96638 73.8623 4.45217 72.7146 5.41659C71.5669 6.38102 70.8469 7.65977 70.5689 9.25286C70.2695 10.8459 70.5261 12.1247 71.3459 13.0891C72.1657 14.0535 73.3847 14.5393 75.0243 14.5393C77.6619 14.5393 79.4584 13.6249 80.4279 11.7961H75.6873L76.3217 8.23843H85.5677L85.5605 8.23128Z"
                    fill="#D1D3D4"
                  />
                  <path
                    d="M94.9918 18.4822C92.3185 18.4822 90.2013 17.5535 88.6258 15.6889C87.0504 13.8244 86.5157 11.5955 87.0076 8.98795C87.4853 6.34472 88.7399 4.18726 90.7858 2.51559C92.8318 0.836781 95.284 0.000946045 98.1498 0.000946045C100.787 0.000946045 102.89 0.929651 104.459 2.79421C106.034 4.65876 106.583 6.88765 106.105 9.49517C105.614 12.1384 104.352 14.2959 102.313 15.9675C100.274 17.6463 97.8361 18.4822 94.9846 18.4822H94.9918ZM95.4409 14.5245C96.9878 14.5245 98.3779 14.0315 99.6183 13.0528C100.859 12.0741 101.614 10.8025 101.899 9.24513C102.178 7.68777 101.899 6.41616 101.073 5.43744C100.238 4.45873 99.0551 3.9658 97.5296 3.9658C96.004 3.9658 94.6425 4.45873 93.4448 5.43744C92.2472 6.41616 91.513 7.68777 91.2278 9.24513C90.9498 10.8168 91.1993 12.0955 91.9906 13.0671C92.7819 14.0387 93.9296 14.5245 95.4409 14.5245Z"
                    fill="#D1D3D4"
                  />
                  <path
                    d="M111.823 14.4173H118.41L117.697 18.4822H106.904L107.624 14.3959L110.162 0.000946045H114.361L111.823 14.4173Z"
                    fill="#D1D3D4"
                  />
                  <path
                    d="M130.193 0.000946045C132.617 0.000946045 134.563 0.936795 136.032 2.80135C137.5 4.6659 138.006 6.90194 137.55 9.50946C137.094 12.1313 135.882 14.2816 133.914 15.9675C131.947 17.6463 129.63 18.4893 126.957 18.4893H119.557L122.815 0.00808993H130.193V0.000946045ZM133.465 9.1094C133.743 7.54489 133.515 6.309 132.781 5.41601C132.04 4.51588 130.935 4.06582 129.459 4.06582H126.294L124.462 14.4173H127.37C128.931 14.4173 130.272 13.9315 131.377 12.9671C132.482 11.9955 133.18 10.7168 133.458 9.1094H133.465Z"
                    fill="#D1D3D4"
                  />
                  <path
                    d="M152.307 4.06582H145.057L144.508 7.15198H151.123L150.41 11.1097H143.809L143.225 14.4102H150.603L149.89 18.475H138.306L141.564 -0.00619507H153.005L152.292 4.05868L152.307 4.06582Z"
                    fill="#D1D3D4"
                  />
                  <path
                    d="M152.278 18.4822L155.564 0.000946045H158.715L163.998 10.381L165.801 0.000946045H170L166.771 18.4822H163.606L158.337 8.10211L156.477 18.4822H152.278Z"
                    fill="#D1D3D4"
                  />
                  <path
                    d="M79.9076 22.5113L79.5155 24.8331H74.0621L71.2248 40.9926H68.7868L71.624 24.8331H66.1991L66.5912 22.5113H79.9076Z"
                    fill="#D1D3D4"
                  />
                  <path
                    d="M82.3455 22.5113H84.7835L81.5257 40.9997H79.0876L82.3455 22.5113Z"
                    fill="#D1D3D4"
                  />
                  <path
                    d="M94.1649 40.9926C91.3704 40.9926 89.2247 40.0567 87.7205 38.1922C86.2164 36.3276 85.7031 34.0916 86.1807 31.4983C86.637 28.9194 87.8987 26.7762 89.9732 25.0688C92.0476 23.3615 94.4001 22.5113 97.0377 22.5113C98.827 22.5113 100.374 22.9043 101.664 23.6829C102.955 24.4616 103.917 25.5332 104.551 26.8905L102.363 28.0121C101.329 25.8761 99.5114 24.8045 96.9094 24.8045C94.8207 24.8045 92.9958 25.4689 91.4417 26.7905C89.8876 28.1121 88.9324 29.7695 88.5831 31.7555C88.2338 33.7558 88.5974 35.4132 89.6738 36.7205C90.7573 38.0279 92.3185 38.6779 94.3787 38.6779C95.7118 38.6779 96.9451 38.3922 98.0643 37.8135C99.1906 37.2349 100.117 36.4276 100.859 35.3918L102.89 36.6419C101.892 37.985 100.616 39.0423 99.0837 39.8281C97.5439 40.6068 95.9114 40.9997 94.172 40.9997L94.1649 40.9926Z"
                    fill="#D1D3D4"
                  />
                  <path
                    d="M121.411 22.5113L111.502 31.6198L118.303 40.9926H115.565L108.957 31.8841L107.353 40.9926H104.915L108.173 22.5042H110.611L109.149 30.8197L118.31 22.5042H121.418L121.411 22.5113Z"
                    fill="#D1D3D4"
                  />
                  <path
                    d="M134.185 24.8331H125.752L124.747 30.5125H132.517L132.097 32.8128H124.341L123.3 38.6779H131.861L131.469 40.9997H120.455L123.728 22.5113H134.585L134.192 24.8331H134.185Z"
                    fill="#D1D3D4"
                  />
                  <path
                    d="M149.469 22.5113L149.077 24.8331H143.624L140.787 40.9926H138.349L141.186 24.8331H135.761L136.153 22.5113H149.469Z"
                    fill="#D1D3D4"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_155_124">
                    <rect width="170" height="41" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            )}
          </Link>
          <div
            className="hidden lg:flex  items-center w-full"
            style={{
              flexDirection: position === "left" ? "row-reverse" : "row",
            }}
          >
            <div
              ref={searchRef}
              className={`relative w-[70%]  ${pathname.includes("category") ? "hidden" : "block"} lg:max-w-[400px]`}
            >
              <SearchInput
                isCart={isStoreModule}
                placeholder={searchPlaceholder}
                className="w-full"
                onFocus={async () => {
                  setShowSearch(true);
                  await fetchHeaderSearchResults();
                }}
                onChange={(e: any) => {
                  setTimeout(() => {
                    setSearchKey(e);
                  }, 1000);
                }}
              />

              {showSearch && (
                <div className="absolute left-0 right-0 top-full z-50 mt-2">
                  <div className="glass-effect rounded-[20px] shadow-lg border border-[#0000000F] overflow-hidden text-start">
                    <div className="px-6 py-4 border-b border-[#0000000F]">
                      <h3 className="text-sm font-semibold text-[#D1D3D4]">
                        {t("hero.searchResults")}
                      </h3>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      {renderSearchResults()}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {
              <div
                className="flex w-full gap-3 xl:gap-6 items-center justify-end"
                style={{
                  marginInlineStart: position !== "left" ? "auto" : "0",
                  marginInlineEnd: position === "left" ? "auto" : "0",
                }}
              >
                <div className="text-sm font-medium flex gap-3 items-start">
                  {showStore === 1 && (
                    <>
                      <span className="w-[0.5px] bg-[#D4AF37] h-[22px] block"></span>
                      <Link
                        href="/store"
                        className="flex justify-start items-center gap-2 group"
                      >
                        <div className="relative overflow-hidden">
                          <Image
                            src="/storeIcon.svg"
                            alt=""
                            width={25}
                            height={25}
                            className="relative z-10"
                          />
                        </div>

                        <span
                          className={`shine-link-auto text-[16px] font-semibold text-nowrap ${isStoreModule ? "!text-[#d9ac4f]" : "!text-[#d9ac4f]"}`}
                        >
                          {t("store.storeName")}
                        </span>
                      </Link>
                      <span className="w-[0.5px] bg-[#D4AF37] h-[22px] block"></span>
                    </>
                  )}

                  <div>
                    <HeaderDropdown
                      options={currencies}
                      value={currency}
                      onChange={(value) => {
                        setCurrency(value);
                        Cookies.set("currency", value, {
                          expires: 365,
                          sameSite: "Lax",
                        });
                        window.location.reload();
                      }}
                    />
                  </div>
                  <span className="w-[0.5px] bg-[#D4AF37] h-[22px] block"></span>
                  <div>
                    <HeaderDropdown
                      options={languages}
                      value={language}
                      onChange={handleLanguageChange}
                      icon={global}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                {!isReferral && (
                  <button
                    onClick={() => {
                      if (token) {
                        profil?.has_bank_data
                          ? (window.location.href = "/seller-home")
                          : (window.location.href = "/seller/bank-details");
                      } else {
                        window.localStorage.setItem("note", "sellTicket");
                        setModalIsOpen(true);
                      }
                    }}
                    className="flex gap-1 xl:gap-2 ltr:gap-1 items-center justify-center bg-transparent border border-[#D4AF37] rounded-[10px] w-[145px] h-[50px] text-[12px] xl:text-sm font-medium text-[#d9ac4f] text-nowrap"
                  >
                    {t("header.sellTicket")}
                    <Image src={ticket} alt="" />
                  </button>
                )}
                {!token && (
                  <button
                    type="button"
                    onClick={() => {
                      setModalIsOpen(true);
                    }}
                    className="relative flex gap-1 xl:gap-2 items-center justify-center text-[#D1D3D4] text-[10px] xl:text-[12px] font-medium rounded-[10px] w-[145px] h-[50px] border border-[#8a867a] text-nowrap overflow-hidden"
                  >
                    <div className="absolute inset-0 -z-10 w-full h-full">
                      <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 145 50"
                        preserveAspectRatio="none"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          width="100%"
                          height="100%"
                          rx="10"
                          fill="#D4AF37"
                          fill-opacity="0.1"
                        />
                      </svg>
                    </div>{" "}
                    {t("header.login")}
                    <Image src={login} alt="" />
                  </button>
                )}
                {token && <UserDropdown />}
              </div>
            }
          </div>
          <div
            className="hidden  lg:hidden w-fit "
            style={{
              flexDirection: position === "left" ? "row-reverse" : "row",
            }}
          >
            <div ref={searchRef} className="relative w-full lg:max-w-[400px]">
              <SearchInput
                isCart={isStoreModule}
                placeholder={searchPlaceholder}
                className="w-full"
                onFocus={async () => {
                  setShowSearch(true);
                  await fetchHeaderSearchResults();
                }}
                onChange={(e: any) => {
                  setTimeout(() => {
                    setSearchKey(e);
                  }, 1000);
                }}
              />

              {showSearch && (
                <div className="absolute left-0 right-0 top-full z-50 mt-2">
                  <div className="glass-effect rounded-[20px] shadow-lg border border-[#0000000F] overflow-hidden text-start">
                    <div className="px-6 py-4 border-b border-[#0000000F]">
                      <h3 className="text-sm font-semibold text-[#D1D3D4]">
                        {t("hero.searchResults")}
                      </h3>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      {renderSearchResults()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-4 items-center lg:hidden  min-w-[120px]">
              <div className="text-[#D1D3D4] text-sm font-medium flex gap-3 items-start">
                <div className={`flex items-center gap-2 ${showStore === 1 ? "flex" : "invisible"}`}>
                  <span className="w-[0.5px] bg-[#D4AF37] h-[22px] block shrink-0"></span>
                  <Link
                    href="/store"
                    className="flex justify-start items-center gap-2 mt-1 shrink-0"
                  >
                    <Image
                      src="/storeIcon.svg"
                      alt=""
                      width={17}
                      height={17}
                      className="shrink-0 min-w-[17px] min-h-[17px]"
                    />
                  </Link>
                  <span className="w-[0.5px] bg-[#D4AF37] h-[22px] block shrink-0"></span>
                </div>

                <div>
                  <HeaderDropdown
                    options={currencies}
                    value={currency}
                    onChange={(value) => {
                      setCurrency(value);
                      Cookies.set("currency", value, {
                        expires: 365,
                        sameSite: "Lax",
                      });
                      window.location.reload();
                    }}
                  />
                </div>
                <span className="w-[0.5px] bg-[#D4AF37] h-[22px] block"></span>
                <div>
                  <HeaderDropdown
                    options={languages}
                    value={language}
                    onChange={handleLanguageChange}
                    icon={global}
                  />
                </div>
              </div>
              {token && (
                <MobileUserMenu IsAppOpen={IsAppOpen} showStore={showStore} />
              )}
              {!token && (
                <button
                  onClick={() => setModalIsOpen(true)}
                  className="flex gap-1 items-center flex-1"
                >
                  <svg
                    className="w-[35px] h-[35px] md:w-[46px] md:h-[46px]"
                    viewBox="0 0 55 55"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g filter="url(#filter0_f_110_189)">
                      <circle
                        cx="27.0474"
                        cy="27.0474"
                        r="22.5474"
                        stroke="#D9AC4F"
                      />
                    </g>
                    <circle
                      cx="27.0474"
                      cy="27.0474"
                      r="22.5474"
                      stroke="#D9AC4F"
                    />
                    <circle
                      cx="27.0472"
                      cy="27.0472"
                      r="20.0412"
                      fill="#111111"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M22.5383 23.2899C22.5383 22.7306 22.6485 22.1768 22.8625 21.6601C23.0765 21.1434 23.3902 20.6739 23.7857 20.2785C24.1812 19.883 24.6506 19.5693 25.1673 19.3553C25.684 19.1413 26.2378 19.0311 26.7971 19.0311C27.3564 19.0311 27.9101 19.1413 28.4268 19.3553C28.9435 19.5693 29.413 19.883 29.8085 20.2785C30.2039 20.6739 30.5176 21.1434 30.7317 21.6601C30.9457 22.1768 31.0558 22.7306 31.0558 23.2899C31.0558 24.4194 30.6072 25.5026 29.8085 26.3013C29.0098 27.0999 27.9266 27.5486 26.7971 27.5486C25.6676 27.5486 24.5844 27.0999 23.7857 26.3013C22.987 25.5026 22.5383 24.4194 22.5383 23.2899ZM26.7971 20.5342C26.0662 20.5342 25.3653 20.8245 24.8485 21.3413C24.3318 21.8581 24.0414 22.559 24.0414 23.2899C24.0414 24.0207 24.3318 24.7216 24.8485 25.2384C25.3653 25.7552 26.0662 26.0455 26.7971 26.0455C27.5279 26.0455 28.2288 25.7552 28.7456 25.2384C29.2624 24.7216 29.5528 24.0207 29.5528 23.2899C29.5528 22.559 29.2624 21.8581 28.7456 21.3413C28.2288 20.8245 27.5279 20.5342 26.7971 20.5342ZM22.7889 30.5548C22.1909 30.5548 21.6174 30.7923 21.1946 31.2152C20.7718 31.638 20.5342 32.2115 20.5342 32.8094V33.9999C20.5342 34.0179 20.5472 34.034 20.5653 34.037C24.6928 34.7103 28.9024 34.7103 33.0289 34.037C33.0374 34.035 33.045 34.0303 33.0506 34.0236C33.0563 34.0169 33.0595 34.0086 33.06 33.9999V32.8094C33.06 32.2115 32.8224 31.638 32.3996 31.2152C31.9768 30.7923 31.4033 30.5548 30.8053 30.5548H30.4646C30.4378 30.5553 30.4112 30.5593 30.3855 30.5668L29.5187 30.8504C27.7502 31.4279 25.8439 31.4279 24.0755 30.8504L23.2077 30.5668C23.1826 30.5595 23.1567 30.5554 23.1306 30.5548H22.7889ZM19.0311 32.8094C19.0311 31.8128 19.427 30.857 20.1317 30.1523C20.8364 29.4476 21.7922 29.0517 22.7889 29.0517H23.1296C23.3166 29.0524 23.4983 29.0811 23.6747 29.1379L24.5425 29.4215C26.0075 29.8998 27.5867 29.8998 29.0517 29.4215L29.9195 29.1379C30.0949 29.0808 30.2792 29.0517 30.4636 29.0517H30.8053C31.8019 29.0517 32.7577 29.4476 33.4624 30.1523C34.1671 30.857 34.563 31.8128 34.563 32.8094V33.9999C34.563 34.7554 34.0159 35.3988 33.2704 35.52C28.9832 36.2198 24.611 36.2198 20.3238 35.52C19.9632 35.4607 19.6354 35.2754 19.3987 34.997C19.162 34.7186 19.0317 34.3653 19.0311 33.9999V32.8094Z"
                      fill="white"
                    />
                    <defs>
                      <filter
                        id="filter0_f_110_189"
                        x="0"
                        y="0"
                        width="54.0947"
                        height="54.0947"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="BackgroundImageFix"
                          result="shape"
                        />
                        <feGaussianBlur
                          stdDeviation="2"
                          result="effect1_foregroundBlur_110_189"
                        />
                      </filter>
                    </defs>
                  </svg>
                  <Image src={arrowDown} alt="" />
                </button>
              )}
            </div>
        </div>
        {modalIsOpen && (
          <LogInModal profil={profil} onClose={() => setModalIsOpen(false)} />
        )}
      </div>
      <div
        className={`xl:container !mx-auto ${isPaddingInHome ? "px-1 md:px-15" : "px-3"} ${shouldHideOnDetails ? "block" : "hidden"}`}
      >
        <ul
          className={` hidden md:flex gap-4 xl:gap-5 justify-start overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] items-center text-[#D1D3D4] text-[12px] xl:text-sm font-medium`}
        >
          <Link
            href={"/"}
            className={`hidden md:block pb-2 text-nowrap ${
              pathname === "/"
                ? "!text-[#d9ac4f] text-[16px]  font-bold  rounded-[2px]"
                : "text-[#D1D3D4] text-[16px]  font-medium"
            }`}
            style={pathname === "/" ? { textShadow: "0 0 10px #D4AF37" } : {}}
          >
            {t("header.events")}
          </Link>

          {!isReferral &&
            data?.header_categories?.slice(0, 6).map((res: any) => (
              <Link
                key={res?.id}
                href={`/category/${res?.slug}`}
                className={`pb-2 text-nowrap ${
                  pathname === `/category/${res?.slug}`
                    ? "!text-[#d9ac4f] text-[16px]  font-bold border-b-2 border-[#D4AF37] rounded-[2px]"
                    : "text-[#D1D3D4] text-[16px]  font-medium"
                }`}
                style={
                  pathname === `/category/${res?.slug}`
                    ? { textShadow: "0 0 10px #D4AF37" }
                    : {}
                }
              >
                {res?.title === "الأكثر مبيعًا"
                  ? t("newHome.bestSelling")
                  : res?.title === "تذاكر دخول الموسم"
                    ? t("newHome.seasonEntryTickets")
                    : res?.title}
              </Link>
            ))}
          {!isReferral && (
            <button
              onClick={() => {
                if (token) {
                  profil?.is_business
                    ? (window.location.href = "/business/dashboard")
                    : (window.location.href = "/business/register");
                } else {
                  window.localStorage.setItem("note", "business");
                  setModalIsOpen(true);
                }
              }}
              className={`hidden md:block text-nowrap ${
                pathname.startsWith("/business")
                  ? "!text-[#d9ac4f] text-[16px] xl:text-[18px] font-bold border-b-2 pb-2 border-[#D4AF37] rounded-[2px]"
                  : "text-[#D1D3D4] text-[16px] pb-2 font-medium"
              }`}
              style={
                pathname.startsWith("/business")
                  ? { textShadow: "0 0 10px #D4AF37" }
                  : {}
              }
            >
              {t("header.business")}
            </button>
          )}
        </ul>
      </div>
    </div>
  );
}
